/**
 * Editor для ref.sales_channels (Порція Роздріб.5b).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import {
  retailSalesChannels,
  type ChannelType,
} from '@/api/retailSalesChannels';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useNextRefCode } from '@/hooks/useNextRefCode';
import RequirePermission from '@/auth/RequirePermission';
import { validateRequired } from '@/utils/validateRequired';
import { extractApiError } from '@/utils/extractApiError';
import RequiredLabel from '@/components/RequiredLabel';
import { useFormDraft } from '@/shared/hooks/useFormDraft';

const PERMISSION = 'retail.sales_channel.manage';

interface FormState {
  organization_id: string;
  code: string;
  name: string;
  channel_type: ChannelType;
  config: string;
  is_active: boolean;
  comment: string;
}

const EMPTY: FormState = {
  organization_id: '',
  code: '',
  name: '',
  channel_type: 'pos',
  config: '{}',
  is_active: true,
  comment: '',
};

export default function SalesChannelEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg(PERMISSION);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const docQuery = useQuery({
    queryKey: ['retail-sales-channel', id],
    queryFn: () => retailSalesChannels.get(id!),
    enabled: !isNew,
  });

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  // Авто-пропозиція коду CH-NN (channel)
  const nextCode = useNextRefCode({
    queryKey: ['retail-sales-channels', form.organization_id, 'for-next-code'],
    queryFn: () =>
      retailSalesChannels.list({ organization_id: form.organization_id, limit: 500 }),
    extractCodes: (data) => data.items.map((i) => i.code),
    prefix: 'CH',
    enabled: isNew && !!form.organization_id,
  });
  useEffect(() => {
    if (isNew && nextCode.suggested && !form.code) {
      setForm((f) => ({ ...f, code: nextCode.suggested }));
    }
  }, [isNew, nextCode.suggested, form.code]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        code: d.code,
        name: d.name,
        channel_type: d.channel_type,
        config: JSON.stringify(d.config ?? {}, null, 2),
        is_active: d.is_active,
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data]);

  const parseConfig = (): Record<string, unknown> => {
    try {
      return JSON.parse(form.config || '{}');
    } catch {
      throw new Error('Невалідний JSON у полі Config');
    }
  };

  const payload = () => ({
    code: form.code,
    name: form.name,
    channel_type: form.channel_type,
    config: parseConfig(),
    is_active: form.is_active,
    comment: form.comment || null,
  });

  const create = useMutation({
    mutationFn: () =>
      retailSalesChannels.create({
        organization_id: form.organization_id,
        ...payload(),
      }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['retail-sales-channels'] });
      navigate(`/retail/sales-channels/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const update = useMutation({
    mutationFn: () => retailSalesChannels.update(id!, payload()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-sales-channels'] });
      qc.invalidateQueries({ queryKey: ['retail-sales-channel', id] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const remove = useMutation({
    mutationFn: () => retailSalesChannels.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-sales-channels'] });
      navigate('/retail/sales-channels');
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const handleSave = () => {
    setError(null);
    try {
      parseConfig();
    } catch (err) {
      setError(extractApiError(err));
      return;
    }
    const err = validateRequired(
      {
        organization_id: form.organization_id,
        code: form.code,
        name: form.name,
      },
      {
        organization_id: 'Юрособа',
        code: 'Код',
        name: 'Назва',
      },
    );
    if (err) { setError(err); return; }
    if (isNew) create.mutate();
    else update.mutate();
  };

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий канал продажу' : `Канал: ${form.name} — ${form.code}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/retail/sales-channels')}>Скасувати</button>
          <RequirePermission code={PERMISSION}>
            <button className="primary" onClick={handleSave} disabled={create.isPending || update.isPending}>
              Зберегти
            </button>
          </RequirePermission>
          {!isNew && (
            <RequirePermission code={PERMISSION}>
              <button
                className="danger"
                onClick={() => {
                  if (window.confirm('Видалити канал?')) remove.mutate();
                }}
              >
                Видалити
              </button>
            </RequirePermission>
          )}
        </div>
      </div>

      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <RequiredLabel>Організація</RequiredLabel>
          {isNew ? (
            <select
              value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            >
              <option value="">—</option>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name} — {o.code}
                </option>
              ))}
            </select>
          ) : (
            <div>
              {orgList.data?.items.find((o) => o.id === form.organization_id)?.name ??
                form.organization_id}
            </div>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <RequiredLabel>Код</RequiredLabel>
          <input
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            maxLength={30}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <RequiredLabel>Назва</RequiredLabel>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            maxLength={255}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Тип каналу</label>
          <select
            value={form.channel_type}
            onChange={(e) =>
              setForm({ ...form, channel_type: e.target.value as ChannelType })
            }
          >
            <option value="pos">Торгова точка (POS)</option>
            <option value="online_store">Інтернет-магазин</option>
            <option value="marketplace_rozetka">Маркетплейс Розетка</option>
            <option value="marketplace_prom">Маркетплейс Prom</option>
            <option value="other">Інше</option>
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            {' '}Активний
          </label>
        </div>
        <div className="field" style={{ gridColumn: 'span 12' }}>
          <label>Config (JSON)</label>
          <textarea
            value={form.config}
            onChange={(e) => setForm({ ...form, config: e.target.value })}
            rows={4}
            style={{ fontFamily: 'monospace' }}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 12' }}>
          <label>Коментар</label>
          <textarea
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            rows={2}
          />
        </div>
      </div>

      {error && (
        <div className="error" style={{ marginTop: '1rem', color: 'var(--badge-red-fg)' }}>
          {error}
        </div>
      )}
    </div>
  );
}
